import { checkFieldExists } from '../pages/Form/CheckFieldExists';
import {
    ComparisonValueType,
    ConditionItem,
} from '../types/generated/formDefinitionItem';
import { parseISO, startOfDay } from 'date-fns';

export const getVisibilityValue = (
    condition: ConditionItem,
    value: any,
    fieldName?: string
): boolean => {
    if (!condition) {
        return true;
    }

    switch (condition?.valueType) {
        case ComparisonValueType.Visible:
            if (fieldName) {
                if (condition?.compareType === '===') {
                    const isFieldRendered = checkFieldExists(fieldName);
                    return !!(
                        (condition.valueToCompare === 'true' &&
                            isFieldRendered) ||
                        (condition.valueToCompare === 'false' &&
                            !isFieldRendered)
                    );
                }
            }
            return false;
        case ComparisonValueType.String:
            if (condition?.compareType === '===') {
                if (condition?.valueToCompare === '' && value === null) {
                    return true;
                }
                // Check if value is a string using typeof
                if (typeof value === 'string') {
                    return value === condition?.valueToCompare;
                }

                // TODO: This should throw an error
                return false; // If value is not a string, return false
            }
            if (condition?.compareType === '!==') {
                return value !== condition?.valueToCompare;
            }
            if (condition?.compareType === 'includes') {
                return (value as string[]).includes(condition?.valueToCompare);
                // } else if (condition?.compareType === 'fullArray') {
                //   return xor(value [],condition?.valueToCompareArray.map(value => value)).length === 0;
            }
            
            if (condition?.compareType === 'in') {
                let isIn = false;
                if (condition.listToCompare) {
                    isIn = condition.listToCompare?.some(
                          (item) => (value as string) === item
                      );
                    }
                return isIn;
            }

            if (condition?.compareType === 'noneIn') {
                if (condition.listToCompare) {
                    return !condition.listToCompare.includes(value as string);
                }
                return true; // If there is no list to compare, return true
            }
            return false;

        case ComparisonValueType.Number:
            if (condition?.additionalCompareType === 'arrayLength') {
                if (condition?.compareType === '===') {
                    return value.length === Number(condition?.valueToCompare);
                }
                if (condition?.compareType === '!==') {
                    return value.length !== Number(condition?.valueToCompare);
                }
                if (condition?.compareType === '>=') {
                    return value.length >= Number(condition?.valueToCompare);
                }
                if (condition?.compareType === '<=') {
                    return value.length <= Number(condition?.valueToCompare);
                }
                if (condition?.compareType === '>') {
                    return value.length > Number(condition?.valueToCompare);
                }
                if (condition?.compareType === '<') {
                    return value.length < Number(condition?.valueToCompare);
                }
            }
            if (condition?.compareType === '===') {
                return (value as number) === Number(condition?.valueToCompare);
            }
            if (condition?.compareType === '!==') {
                return (value as number) !== Number(condition?.valueToCompare);
            }
            if (condition?.compareType === '>=') {
                return (value as number) >= Number(condition?.valueToCompare);
            }
            if (condition?.compareType === '<=') {
                return (value as number) <= Number(condition?.valueToCompare);
            }
            if (condition?.compareType === '>') {
                return (value as number) > Number(condition?.valueToCompare);
            }
            if (condition?.compareType === '<') {
                return (value as number) < Number(condition?.valueToCompare);
            }
            if (condition?.compareType === 'includes') {
                return (value as number[]).includes(
                    Number(condition?.valueToCompare)
                );
                // } else if (condition?.compareType === 'fullArray') {
                //   return xor(value as number[],condition?.valueToCompareArray.map(value => Number(value))).length === 0;
            }
            return false;

        case ComparisonValueType.Boolean: {
            const valueToParse =
                (value ?? 'false') === '' ? 'false' : value ?? 'false';

            if (condition?.compareType === '===') {
                return (
                    JSON.parse(valueToParse) ===
                    (condition?.valueToCompare.toLowerCase() === 'true')
                );
            }
            if (condition?.compareType === '!==') {
                return (
                    JSON.parse(valueToParse) !==
                    (condition?.valueToCompare.toLowerCase() === 'true')
                );
            }
            if (condition?.compareType === 'includes') {
                return (value as boolean[]).includes(
                    condition?.valueToCompare.toLowerCase() === 'true'
                );
                // } else if (condition?.compareType === 'fullArray') {
                //   return xor(value as boolean[],condition?.valueToCompareArray.map(value => Boolean(value))).length === 0;
            }
            return false;
        }

        case ComparisonValueType.BooleanV2: {
            const parsedValue = `${value}`.toLowerCase();
            const valueToCompare = condition?.valueToCompare?.toLowerCase();

            if (condition?.compareType === '===') {
                return parsedValue === valueToCompare;
            }

            if (condition?.compareType === '!==') {
                return parsedValue !== valueToCompare;
            }

            return false;
        }

        case ComparisonValueType.Array: {
            const userInputArray = value as string[];

            if (condition?.compareType === 'in') {
                if (!userInputArray) {
                    // value is not a string array, so it can't be present in listToCompare,
                    // thus the field should not be visible
                    return false;
                }

                return condition.listToCompare
                    ? condition.listToCompare.some((item) => userInputArray.includes(item))
                    : false;
            }

            if (condition?.compareType === 'noneIn') {
                if (!userInputArray) {
                    // value is not a string array, so it is not present in listToCompare,
                    // thus the field should be visible
                    return true;
                }

                if (!condition.listToCompare) {
                    return true;
                }

                return condition.listToCompare.every(
                    (item) => !userInputArray.includes(item)
                );
            }

            return false;
        }

        case ComparisonValueType.DateOnly: {
            if (!value) return false;

            const fieldDate = startOfDay(parseISO(value));
            const compareDate =
                condition?.valueToCompare === 'today'
                    ? startOfDay(new Date())
                    : startOfDay(parseISO(condition?.valueToCompare));

            if (Number.isNaN(fieldDate.getTime()) || Number.isNaN(compareDate.getTime())) {
                return false;
            }

            const fieldTime = fieldDate.getTime();
            const compareTime = compareDate.getTime();

            if (condition?.compareType === '===') {
                return fieldTime === compareTime;
            }
            if (condition?.compareType === '!==') {
                return fieldTime !== compareTime;
            }
            if (condition?.compareType === '>=') {
                return fieldTime >= compareTime;
            }
            if (condition?.compareType === '<=') {
                return fieldTime <= compareTime;
            }
            if (condition?.compareType === '>') {
                return fieldTime > compareTime;
            }
            if (condition?.compareType === '<') {
                return fieldTime < compareTime;
            }
            return false;
        }

        // //might be a problem when comparing also times when there is a time difference and midnight happens to be inbetween those times
        // case ComparisonValueType.datetime:
        //     if (condition?.compareType === '===') {
        //         return dayjs(value ).isSame(
        //             dayjs(condition?.valueToCompare)
        //         );
        //     } else if (condition?.compareType === '!==') {
        //         return !dayjs(value ).isSame(
        //             dayjs(condition?.valueToCompare)
        //         );
        //     } else if (condition?.compareType === '>=') {
        //         return !dayjs(value ).isAfter(
        //             dayjs(condition?.valueToCompare) ||
        //                 dayjs(value ).isSame(
        //                     dayjs(condition?.valueToCompare)
        //                 )
        //         );
        //     } else if (condition?.compareType === '<=') {
        //         return !dayjs(value ).isBefore(
        //             dayjs(condition?.valueToCompare) ||
        //                 dayjs(value ).isSame(
        //                     dayjs(condition?.valueToCompare)
        //                 )
        //         );
        //     } else if (condition?.compareType === '>') {
        //         return !dayjs(value ).isAfter(
        //             dayjs(condition?.valueToCompare)
        //         );
        //     } else if (condition?.compareType === '<') {
        //         return !dayjs(value ).isBefore(
        //             dayjs(condition?.valueToCompare)
        //         );
        //     } else {
        //         return false;
        //     }
        default:
            return false;
    }
};
// const date = dayjs('2011-01-20');
